import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UsePlanPageScrollingProps {
  isLoadingTaxYears: boolean;
  isLoadingUserPlans: boolean;
  displayedYears: any[];
}

export const usePlanPageScrolling = ({
  isLoadingTaxYears,
  isLoadingUserPlans,
  displayedYears,
}: UsePlanPageScrollingProps) => {
  const [searchParams] = useSearchParams();
  const hasScrolledRef = useRef(false);

  // Get the year parameter for use in effects
  const yearParamForEffect = searchParams.get('year');

  useEffect(() => {
    // This effect runs when yearParamForEffect (the actual year value from URL) changes.
    // We reset the scroll lock so that if the user navigates
    // from /plans?year=2023 to /plans?year=2024, it will scroll again.
    hasScrolledRef.current = false;
  }, [yearParamForEffect]);

  // Scroll to year if specified in URL, data is loaded, and not already scrolled
  useEffect(() => {
    if (
      yearParamForEffect &&
      !isLoadingTaxYears &&
      !isLoadingUserPlans &&
      displayedYears &&
      displayedYears.length > 0 &&
      !hasScrolledRef.current
    ) {
      const elementId = `year-${yearParamForEffect}`;
      const yearElement = document.getElementById(elementId);

      if (yearElement) {
        setTimeout(() => {
          yearElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          hasScrolledRef.current = true;
        }, 100);
      }
    }
  }, [yearParamForEffect, isLoadingTaxYears, isLoadingUserPlans, displayedYears]);

  const scrollYearIntoView = (year: string) => {
    const yearElement = document.getElementById(`tax-year-${year}`);
    if (yearElement) {
      setTimeout(() => {
        yearElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        hasScrolledRef.current = true;
      }, 100);
    }
  };

  return {
    hasScrolledRef,
    scrollYearIntoView,
  };
};
